import React, { useState } from 'react';
import axios from 'axios';
import { BACKEND_URL, HASURA_URL } from '../config';

const LOG_ENABLED = process.env.REACT_APP_ENABLE_LOG === 'true'; // Using REACT_APP_ prefix

const ImportarPersonal = ({ onVolver }) => {
  const [file, setFile] = useState(null);
  const [type, setType] = useState('CIVIL'); // 'MILITAR' or 'CIVIL'
  const [status, setStatus] = useState('idle'); // idle, uploading, success, error
  const [report, setReport] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [previewHeaders, setPreviewHeaders] = useState([]);
  const [previewRows, setPreviewRows] = useState([]);
  const [previewError, setPreviewError] = useState('');
  const [isParsing, setIsParsing] = useState(false);

  const getApiBaseUrl = () => (BACKEND_URL || HASURA_URL || '').replace(/\/+$/, '');

  const resetPreview = () => {
    setPreviewHeaders([]);
    setPreviewRows([]);
    setPreviewError('');
  };

  const parsePreview = async (selectedFile) => {
    setIsParsing(true);
    resetPreview();
    try {
      const apiBaseUrl = getApiBaseUrl();
      if (!apiBaseUrl) throw new Error('No se encontro la URL del backend.');

      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('type', type);

      const response = await axios.post(`${apiBaseUrl}/api/import/preview`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const headers = response.data?.headers || [];
      const rows = response.data?.rows || [];
      if (!headers.length) throw new Error('No se pudo generar la vista previa.');
      setPreviewHeaders(headers);
      setPreviewRows(rows);
    } catch (err) {
      setPreviewError(err.message || 'No se pudo leer el archivo.');
    } finally {
      setIsParsing(false);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      setFile(e.target.files[0]);
      setStatus('idle');
      setReport(null);
      setErrorMsg('');
      parsePreview(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      alert('Seleccione un archivo primero.');
      return;
    }

    setStatus('uploading');
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);

    const apiBaseUrl = getApiBaseUrl();
    const uploadUrl = `${apiBaseUrl}/api/import/personal`;

    if (LOG_ENABLED) console.log('📤 Iniciando importación... URL:', uploadUrl, 'Tipo:', type, 'Archivo:', file.name);

    try {
      const response = await axios.post(uploadUrl, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (LOG_ENABLED) console.log('✅ Respuesta importación:', response.data);

      if (response.data.success) {
        setStatus('success');
        setReport(response.data.stats);
      } else {
        setStatus('error');
        setErrorMsg(response.data.message || 'Error desconocido en el servidor.');
      }
    } catch (err) {
      if (LOG_ENABLED) console.error('❌ Error en importación:', err.response?.data || err.message);
      setStatus('error');
      setErrorMsg(err.response?.data?.error || err.message || 'Error de conexión');
    }
  };

  return (
    <div className="container mt-4" data-gesture-ignore="true">
      <div className="card shadow-sm">
        <div className="card-header bg-secondary text-white d-flex justify-content-between align-items-center">
          <h4 className="mb-0">📥 Importar Personal (Excel)</h4>
          <button className="btn btn-sm btn-light text-secondary" onClick={onVolver}>
            <i className="bi bi-x-lg"></i> Cerrar
          </button>
        </div>
        <div className="card-body">
          
          <div className="row g-4">
            <div className="col-md-6">
              <label className="form-label">1. Seleccione el Tipo de Personal</label>
              <select className="form-select mb-3" value={type} onChange={(e) => setType(e.target.value)}>
                <option value="CIVIL">Personal Civil (No Militar)</option>
                <option value="MILITAR">Personal Militar (Con Grado/Componente)</option>
              </select>

              <label className="form-label">2. Subir Archivo Excel (.xlsx)</label>
              <input
                type="file"
                className="form-control mb-3"
                accept=".xlsx, .xls"
                onChange={handleFileChange}
                onClick={(e) => { e.target.value = null; }}
              />

              <button 
                className="btn btn-primary w-100" 
                onClick={handleUpload} 
                disabled={!file || status === 'uploading' || isParsing}
              >
                {status === 'uploading' ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Procesando...
                  </>
                ) : (
                  <>
                    <i className="bi bi-cloud-upload me-2"></i> Procesar Importacion
                  </>
                )}
              </button>
              
              {status === 'error' && (
                <div className="alert alert-danger mt-3">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  {errorMsg}
                </div>
              )}
            </div>

            <div className="col-md-6">
              <div className="card bg-light h-100">
                <div className="card-body">
                  <h6 className="card-title text-muted">Vista Previa</h6>

                  {isParsing && (
                    <div className="text-muted small">
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Leyendo archivo...
                    </div>
                  )}

                  {!isParsing && previewError && (
                    <div className="alert alert-warning mt-2">
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      {previewError}
                    </div>
                  )}

                  {!isParsing && !previewError && previewHeaders.length === 0 && (
                    <p className="text-muted small">Seleccione un archivo para ver la vista previa.</p>
                  )}

                  {!isParsing && previewHeaders.length > 0 && (
                    <div className="table-responsive mb-3" style={{ maxHeight: '220px' }}>
                      <table className="table table-sm table-bordered bg-white">
                        <thead className="table-light">
                          <tr>
                            {previewHeaders.map((header, idx) => (
                              <th key={`header-${idx}`} className="small text-muted">{header || '-'}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {previewRows.map((row, rowIdx) => (
                            <tr key={`row-${rowIdx}`}>
                              {previewHeaders.map((_, colIdx) => (
                                <td key={`cell-${rowIdx}-${colIdx}`} className="small">
                                  {row[colIdx] !== undefined && row[colIdx] !== null ? String(row[colIdx]) : ''}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <h6 className="card-title text-muted">Resultado de la Importación</h6>
                  
                  {status === 'idle' && <p className="text-muted small">Esperando procesamiento...</p>}
                  
                  {status === 'success' && report && (
                    <div>
                      <div className="alert alert-success">
                        <strong>✅ Importación Exitosa</strong>
                      </div>
                      <ul className="list-group list-group-flush">
                        <li className="list-group-item d-flex justify-content-between align-items-center">
                          Total Procesado
                          <span className="badge bg-secondary rounded-pill">{report.total}</span>
                        </li>
                        <li className="list-group-item d-flex justify-content-between align-items-center">
                          Nuevos Insertados
                          <span className="badge bg-success rounded-pill">{report.inserted}</span>
                        </li>
                        <li className="list-group-item d-flex justify-content-between align-items-center">
                          Omitidos (Duplicados)
                          <span className="badge bg-warning text-dark rounded-pill">{report.skipped}</span>
                        </li>
                        <li className="list-group-item d-flex justify-content-between align-items-center">
                          Errores
                          <span className="badge bg-danger rounded-pill">{report.errors}</span>
                        </li>
                      </ul>

                      {report.details && report.details.length > 0 && (
                        <div className="mt-3">
                          <label className="form-label small fw-bold">Detalles / Errores:</label>
                          <div className="bg-white border p-2 small text-muted" style={{maxHeight: '150px', overflowY: 'auto'}}>
                            {report.details.map((msg, i) => (
                              <div key={i} className="text-danger mb-1">• {msg}</div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default ImportarPersonal;
